/// <amd-module name="Repos/Files/ContentPreview" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

/**
 * Describes what the default behavior should be when showing files of the
 * type that a content preview handles.
 */
export enum ContentPreviewDefaultBehavior {
    ShowRawText = 1,
    ShowRenderedContent = 2
}

export interface IContentPreviewContribution {
    contributionId: string;
    defaultBehavior: ContentPreviewDefaultBehavior;
    fileExtensions: string[];
    registeredObjectId?: string;
}

export interface IContentPreviewService extends IVssService {
    getPreviewContribution(path: string): IContentPreviewContribution | undefined;
}

const contentPreviewContributionId = "ms.vss-code-web.content-renderer-collection";
const contentPreviewContributionType = "ms.vss-code-web.content-renderer";

class ContentPreviewService extends VssService implements IContentPreviewService {
    private previewContributionExtensions: { [ext: string]: string };
    private previewContributions: { [id: string]: IContentPreviewContribution };

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        // Get all the content preview contribution ids sent to the client
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const contributionIds = contributionService.getContributionChildren(contentPreviewContributionId, contentPreviewContributionType);

        // Keep ids by file extension and preview contributions by contribution id
        this.previewContributions = {};
        this.previewContributionExtensions = {};
        if (contributionIds) {
            for (const id of contributionIds) {
                const contribution = contributionService.getContribution<IContentPreviewContribution>(id);
                if (contribution) {
                    contribution.contributionId = id;
                    for (const extension of contribution.fileExtensions || []) {
                        this.previewContributionExtensions[extension.toUpperCase()] = id;
                        this.previewContributions[id] = contribution;
                    }
                }
            }
        }
    }

    public getPreviewContribution(path: string): IContentPreviewContribution | undefined {
        const contributionId = this.getPreviewContributionId(path);
        if (contributionId) {
            return this.previewContributions[contributionId];
        }
    }

    private getPreviewContributionId(itemPath: string): string | undefined {
        // Since we built the extension map using upper case, we transform the
        // path to upper case too
        itemPath = itemPath.toUpperCase();

        // Get all the extensions from the path
        const extensions = itemPath.split(".").slice(1);
        for (let i = 0; i < extensions.length; i++) {
            // Combine extensions starting from the current index
            const ext = extensions.slice(i).join(".");

            // See any content preview contribution exists for this extension
            const contributionId = this.previewContributionExtensions[ext];
            if (contributionId) {
                return contributionId;
            }
        }
    }
}

const serviceName = "IContentPreviewService";
Services.add(serviceName, { serviceFactory: ContentPreviewService });

export function getContentPreviewService(pageContext: IVssPageContext): IContentPreviewService {
    return pageContext.getService<IContentPreviewService>(serviceName);
}
